import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, Search } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { Textarea } from 'components/ui/Textarea'
import { Select } from 'components/ui/Select'
import { useToast } from 'contexts/ToastContext'
import { emailConfigApi, type EmailTemplate } from 'lib/api'
import { formatShortListDate } from 'lib/utils'
import {
  filterEmailTemplatesForDisplay,
  getDefaultTemplateLanguage,
  getEmailTemplateDisplayLanguage,
  getEmailTemplateDisplayName,
  isFixedFrenchInterviewInvitation,
} from 'lib/emailTemplates'
import { PageHeader } from 'components/layout/PageHeader'

function SortArrows({ className }: { className?: string }) {
  return (
    <svg
      width="7"
      height="9"
      viewBox="0 0 7 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path d="M3.5 0L7 3.5H0L3.5 0Z" fill="currentColor" />
      <path d="M3.5 9L0 5.5H7L3.5 9Z" fill="currentColor" />
    </svg>
  )
}

const getTemplateTypes = (t: (key: string) => string) => [
  { value: 'candidate_notification', label: t('settings.emailTemplatesPage.templateTypes.candidate_notification') },
  { value: 'interview_invitation', label: t('settings.emailTemplatesPage.templateTypes.interview_invitation') },
  { value: 'interview_completion', label: t('settings.emailTemplatesPage.templateTypes.interview_completion') },
  { value: 'weekly_report', label: t('settings.emailTemplatesPage.templateTypes.weekly_report') },
  { value: 'ai_matching', label: t('settings.emailTemplatesPage.templateTypes.ai_matching') },
  { value: 'welcome', label: t('settings.emailTemplatesPage.templateTypes.welcome') },
  { value: 'offer_letter', label: t('settings.emailTemplatesPage.templateTypes.offer_letter') },
  { value: 'rejection', label: t('settings.emailTemplatesPage.templateTypes.rejection') },
  { value: 'screening', label: t('settings.emailTemplatesPage.templateTypes.screening') },
  { value: 'assessment', label: t('settings.emailTemplatesPage.templateTypes.assessment') },
  { value: 'organization_admin_invitation', label: t('settings.emailTemplatesPage.templateTypes.organization_admin_invitation') },
]

type MenuAction = 'view' | 'edit' | 'toggle' | 'duplicate' | 'delete'

function TableRowMenu({
  anchorRect,
  onClose,
  onAction,
  isActive,
  t
}: {
  anchorRect: DOMRect
  onClose: () => void
  onAction: (action: MenuAction) => void
  isActive: boolean
  t: (key: string) => string
}) {
  const menuRef = useRef<HTMLDivElement>(null)

  const style = useMemo(() => {
    const menuWidth = 170
    const menuHeight = 225
    const gap = 8

    let left = anchorRect.right - menuWidth
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))

    let top = anchorRect.bottom + gap
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, anchorRect.top - menuHeight - gap)
    }

    return { left, top }
  }, [anchorRect])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onMouseDown = (e: MouseEvent) => {
      const el = menuRef.current
      if (!el) return
      if (!el.contains(e.target as Node)) onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [onClose])

  const items: Array<{ label: string; tone?: 'default' | 'danger'; action: MenuAction }> = [
    { action: 'view', label: t('settings.emailTemplatesFigmaPage.menu.view') },
    { action: 'edit', label: t('settings.emailTemplatesFigmaPage.menu.edit') },
    {
      action: 'toggle',
      label: isActive
        ? t('settings.emailTemplatesFigmaPage.menu.deactivate')
        : t('settings.emailTemplatesFigmaPage.menu.activate')
    },
    { action: 'duplicate', label: t('settings.emailTemplatesFigmaPage.menu.duplicate') },
    { action: 'delete', label: t('settings.emailTemplatesFigmaPage.menu.delete'), tone: 'danger' }
  ]

  return (
    <div
      ref={menuRef}
      className="fixed z-[80] w-[170px] rounded-[10px] border border-[#D8D8D8] bg-white py-3 dark:border-border dark:bg-card"
      style={{ left: style.left, top: style.top }}
      role="menu"
    >
      <div className="flex flex-col">
        {items.map((item, idx) => (
          <React.Fragment key={item.label}>
            <button
              type="button"
              className={`w-[170px] h-[21px] px-4 flex items-center justify-center text-center text-[14px] leading-[150%] font-medium ${
                item.tone === 'danger'
                  ? 'text-[#E00000] dark:text-red-400'
                  : 'text-[#3F3F3F] dark:text-foreground'
              }`}
              onClick={() => {
                onAction(item.action)
                onClose()
              }}
              role="menuitem"
            >
              {item.label}
            </button>
            {idx < items.length - 1 && <div className="w-[170px] border-t border-[#D8D8D8] my-1.5 dark:border-border" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export function EmailTemplatesFigma() {
  const { t, i18n } = useTranslation()
  const { addToast } = useToast()
  const TEMPLATE_TYPES = getTemplateTypes(t)

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingTemplate, setDeletingTemplate] = useState<EmailTemplate | null>(null)

  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_type: 'welcome',
    language: 'fr' as 'fr' | 'en',
    subject: '',
    body: '',
    is_active: true
  })

  const displayLanguage = useMemo(
    () => getEmailTemplateDisplayLanguage(i18n.language),
    [i18n.language]
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, displayLanguage])

  useEffect(() => {
    loadTemplates()
  }, [displayLanguage])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const templatesData = await emailConfigApi.getAllTemplates({ language: displayLanguage })
      setTemplates(Array.isArray(templatesData) ? templatesData : [])
    } catch (error) {
      console.error('Error loading templates:', error)
      setTemplates([])
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('settings.emailTemplatesPage.errors.loadFailed'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    return filterEmailTemplatesForDisplay(templates, displayLanguage).filter((template) => {
      if (!normalizedSearch) return true
      const displayName = getEmailTemplateDisplayName(template, displayLanguage, t).toLowerCase()
      return (
        displayName.includes(normalizedSearch) ||
        template.name.toLowerCase().includes(normalizedSearch) ||
        template.subject.toLowerCase().includes(normalizedSearch)
      )
    })
  }, [templates, displayLanguage, searchTerm, t])

  const totalItems = filteredTemplates.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage
  const paginatedTemplates = filteredTemplates.slice(startIndex, startIndex + itemsPerPage)
  const endIndex = Math.min(startIndex + paginatedTemplates.length, totalItems)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const currentPageTemplateIds = paginatedTemplates.map((template) => template.id)
  const areAllCurrentPageSelected =
    currentPageTemplateIds.length > 0 && currentPageTemplateIds.every((id) => selectedTemplateIds.includes(id))

  const toggleSelectAllCurrentPage = () => {
    if (areAllCurrentPageSelected) {
      setSelectedTemplateIds((prev) => prev.filter((id) => !currentPageTemplateIds.includes(id)))
      return
    }

    setSelectedTemplateIds((prev) => {
      const merged = new Set(prev)
      currentPageTemplateIds.forEach((id) => merged.add(id))
      return Array.from(merged)
    })
  }

  const toggleTemplateSelection = (templateId: number) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId) ? prev.filter((id) => id !== templateId) : [...prev, templateId]
    )
  }

  const openMenu = (id: number, el: HTMLButtonElement | null) => {
    if (!el) return
    setOpenMenuId(id)
    setMenuAnchorRect(el.getBoundingClientRect())
  }

  const closeMenu = () => {
    setOpenMenuId(null)
    setMenuAnchorRect(null)
  }

  const handleCreate = () => {
    setEditingTemplate(null)
    setTemplateForm({
      name: '',
      template_type: 'welcome',
      language: getDefaultTemplateLanguage('welcome', displayLanguage),
      subject: '',
      body: '',
      is_active: true
    })
    setShowModal(true)
  }

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template)
    setTemplateForm({
      name: template.name,
      template_type: template.template_type,
      language: template.language || 'fr',
      subject: template.subject,
      body: template.body,
      is_active: template.is_active
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      addToast({
        type: 'error',
        title: t('settings.emailTemplatesPage.errors.validationError'),
        message: t('settings.emailTemplatesPage.errors.fillRequiredFields'),
        duration: 3000
      })
      return
    }

    try {
      if (editingTemplate) {
        await emailConfigApi.updateTemplate(editingTemplate.id, templateForm)
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('settings.emailTemplatesPage.success.templateUpdated'),
          duration: 3000
        })
      } else {
        await emailConfigApi.createTemplate(templateForm)
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('settings.emailTemplatesPage.success.templateCreated'),
          duration: 3000
        })
      }
      setShowModal(false)
      setEditingTemplate(null)
      await loadTemplates()
    } catch (error: any) {
      console.error('Error saving template:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.detail || t('settings.emailTemplatesPage.errors.saveFailed'),
        duration: 5000
      })
    }
  }

  const handleDelete = async () => {
    if (!deletingTemplate) return
    try {
      await emailConfigApi.deleteTemplate(deletingTemplate.id)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('settings.emailTemplatesPage.success.templateDeleted'),
        duration: 3000
      })
      setShowDeleteModal(false)
      setDeletingTemplate(null)
      await loadTemplates()
    } catch (error: any) {
      console.error('Error deleting template:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.detail || t('settings.emailTemplatesPage.errors.deleteFailed'),
        duration: 5000
      })
    }
  }

  const handleRowAction = async (template: EmailTemplate, action: MenuAction) => {
    switch (action) {
      case 'view':
      case 'edit':
        handleEdit(template)
        break
      case 'toggle':
        try {
          await emailConfigApi.updateTemplate(template.id, { ...template, is_active: !template.is_active })
          await loadTemplates()
        } catch (error) {
          addToast({
            type: 'error',
            title: t('common.error'),
            message: t('settings.emailTemplatesPage.errors.saveFailed'),
            duration: 5000
          })
        }
        break
      case 'duplicate':
        try {
          await emailConfigApi.createTemplate({
            name: `${template.name} (${t('settings.emailTemplatesFigmaPage.copySuffix')})`,
            template_type: template.template_type,
            language: template.language || 'fr',
            subject: template.subject,
            body: template.body,
            is_active: template.is_active
          })
          await loadTemplates()
        } catch (error) {
          addToast({
            type: 'error',
            title: t('common.error'),
            message: t('settings.emailTemplatesPage.errors.saveFailed'),
            duration: 5000
          })
        }
        break
      case 'delete':
        setDeletingTemplate(template)
        setShowDeleteModal(true)
        break
    }
  }

  const invitationLocked = Boolean(editingTemplate && isFixedFrenchInterviewInvitation(editingTemplate))

  return (
    <div className="w-full space-y-4 flex flex-col overflow-x-hidden">
      <PageHeader title={t('settings.emailTemplatesFigmaPage.title')} />

      <div className="w-full flex items-center justify-between gap-4">
        <div className="w-[389px] h-10 rounded-[6px] border border-[#D8D8D8] bg-[#FAFAFA] px-2 flex items-center gap-2 dark:border-border dark:bg-muted">
          <Search className="h-6 w-6 text-[#7F7F7F] dark:text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('settings.emailTemplatesFigmaPage.searchPlaceholder')}
            className="w-full bg-transparent border-0 outline-none text-[14px] leading-[150%] font-medium text-[#7F7F7F] placeholder:text-[#7F7F7F] dark:text-foreground dark:placeholder:text-muted-foreground p-0 h-auto shadow-none"
          />
        </div>

        <Button
          onClick={handleCreate}
          className="w-[144px] h-[45px] rounded-[10px] border-transparent bg-[#19A6A6] px-4 py-3 gap-2 text-[14px] leading-[150%] tracking-[0.01em] font-medium text-white hover:bg-[#19A6A6]/90 dark:hover:bg-[#19A6A6]/80"
        >
          {t('settings.emailTemplatesFigmaPage.createTemplate')}
        </Button>
      </div>

      <div className="w-full">
        <div className="w-full h-10 bg-[#F4F4F4] dark:bg-muted">
          <div className="w-full h-10 grid grid-cols-[1fr_1fr_1fr_1fr_1fr_32px] items-center py-2 pr-2 pl-2 text-[12px] leading-[150%] font-medium text-[#7F7F7F] dark:text-muted-foreground">
            <div className="flex items-center gap-2 min-w-0">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border accent-[#19A6A6]"
                checked={areAllCurrentPageSelected}
                onChange={toggleSelectAllCurrentPage}
              />
              <span className="inline-flex items-center gap-2 h-[18px] text-[#7F7F7F] dark:text-muted-foreground">
                <SortArrows className="text-[#7F7F7F] dark:text-muted-foreground" />
                {t('settings.emailTemplatesFigmaPage.columns.name')}
              </span>
            </div>
            <span className="inline-flex items-center gap-2 h-[18px] text-[#7F7F7F] dark:text-muted-foreground">
              <SortArrows className="text-[#7F7F7F] dark:text-muted-foreground" />
              {t('settings.emailTemplatesFigmaPage.columns.createdAt')}
            </span>
            <span className="inline-flex items-center gap-2 h-[18px] text-[#7F7F7F] dark:text-muted-foreground">
              <SortArrows className="text-[#7F7F7F] dark:text-muted-foreground" />
              {t('settings.emailTemplatesFigmaPage.columns.type')}
            </span>
            <span className="inline-flex items-center gap-2 h-[18px] text-[#7F7F7F] dark:text-muted-foreground">
              <SortArrows className="text-[#7F7F7F] dark:text-muted-foreground" />
              {t('settings.emailTemplatesFigmaPage.columns.processStep')}
            </span>
            <span className="inline-flex items-center gap-2 h-[18px] text-[#7F7F7F] dark:text-muted-foreground">
              <SortArrows className="text-[#7F7F7F] dark:text-muted-foreground" />
              {t('settings.emailTemplatesFigmaPage.columns.status')}
            </span>
            <span className="justify-self-end" />
          </div>
        </div>

        {loading ? (
          <div className="w-full h-10 flex items-center pl-2 text-[12px] text-muted-foreground">{t('settings.emailTemplatesPage.loadingTemplates')}</div>
        ) : filteredTemplates.length === 0 ? (
          <div className="w-full h-10 flex items-center pl-2 text-[12px] text-muted-foreground">{t('settings.emailTemplatesPage.noTemplatesFound')}</div>
        ) : (
          paginatedTemplates.map((template) => (
            <div
              key={template.id}
              className="w-full border-b border-[#EFEFEF] h-10 grid grid-cols-[1fr_1fr_1fr_1fr_1fr_32px] items-center bg-white pl-2 pr-2 text-[12px] text-[#7F7F7F] hover:bg-[#FAFAFA] dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:bg-muted/40"
            >
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-[#19A6A6]"
                  checked={selectedTemplateIds.includes(template.id)}
                  onChange={() => toggleTemplateSelection(template.id)}
                />
                <span className="truncate text-[#3F3F3F] dark:text-foreground">
                  {getEmailTemplateDisplayName(template, displayLanguage, t)}
                </span>
              </div>
              <span>{formatShortListDate(template.created_at, i18n.language)}</span>
              <span>{TEMPLATE_TYPES.find((tt) => tt.value === template.template_type)?.label || template.template_type}</span>
              <span>{t('status.screening')}</span>
              <span>
                <span
                  className={`inline-flex rounded-full px-2 py-[2px] text-[10px] font-semibold ${
                    template.is_active
                      ? 'bg-[#23B95C] text-white dark:bg-green-600'
                      : 'bg-[#7F7F7F] text-white dark:bg-muted dark:text-muted-foreground'
                  }`}
                >
                  {template.is_active ? t('common.active') : t('common.inactive')}
                </span>
              </span>
              <button
                type="button"
                className="justify-self-end text-[#7F7F7F] hover:text-[#3F3F3F] dark:text-muted-foreground dark:hover:text-foreground"
                onClick={(e) => openMenu(template.id, e.currentTarget)}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </div>
          ))
        )}

        {!loading && totalItems > 0 && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-[#EFEFEF] dark:border-border">
            <div className="text-xs text-muted-foreground">
              {t('settings.emailTemplatesFigmaPage.showingResults', {
                start: startIndex + 1,
                end: endIndex,
                total: totalItems
              })}
            </div>
            {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="h-8 text-xs px-2"
              >
                {t('common.previous')}
              </Button>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className="w-8 h-8 text-xs p-0"
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="h-8 text-xs px-2"
              >
                {t('common.next')}
              </Button>
            </div>
            )}
          </div>
        )}
      </div>

      {openMenuId && menuAnchorRect && (
        <TableRowMenu
          anchorRect={menuAnchorRect}
          onClose={closeMenu}
          t={t}
          isActive={Boolean(templates.find((template) => template.id === openMenuId)?.is_active)}
          onAction={(action) => {
            const template = templates.find((item) => item.id === openMenuId)
            if (!template) return
            handleRowAction(template, action)
          }}
        />
      )}

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false)
            setEditingTemplate(null)
          }}
          title={editingTemplate ? t('settings.emailTemplatesPage.editTemplate') : t('settings.emailTemplatesPage.createEmailTemplate')}
          className="max-w-4xl max-[1149px]:max-w-[95vw] max-[1149px]:max-h-[95vh]"
        >
          <div className="space-y-6 max-[1149px]:space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 max-[1149px]:text-xs max-[1149px]:mb-1.5">
                  {t('settings.emailTemplatesPage.templateNameRequired')}
                </label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('settings.emailTemplatesPage.templateNamePlaceholder')}
                  required
                  className="max-[1149px]:h-9 max-[1149px]:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 max-[1149px]:text-xs max-[1149px]:mb-1.5">
                  {t('settings.emailTemplatesPage.templateTypeRequired')}
                </label>
                <Select
                  value={templateForm.template_type}
                  onChange={(e) => setTemplateForm((prev) => ({
                    ...prev,
                    template_type: e.target.value,
                    language: getDefaultTemplateLanguage(e.target.value, displayLanguage),
                  }))}
                  options={TEMPLATE_TYPES.map((type) => ({
                    value: type.value,
                    label: type.label
                  }))}
                  required
                  disabled={invitationLocked}
                  className="max-[1149px]:h-9 max-[1149px]:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2 max-[1149px]:text-xs max-[1149px]:mb-1.5">
                {t('settings.emailTemplatesPage.emailSubjectRequired')}
              </label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder={t('settings.emailTemplatesPage.emailSubjectPlaceholder')}
                required
                readOnly={invitationLocked}
                className="max-[1149px]:h-9 max-[1149px]:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2 max-[1149px]:text-xs max-[1149px]:mb-1.5">
                {t('settings.emailTemplatesPage.emailBodyRequired')}
              </label>
              {invitationLocked && (
                <p className="mb-2 text-sm text-muted-foreground max-[1149px]:text-xs">
                  {t('settings.emailTemplatesPage.fixedInvitationNotice')}
                </p>
              )}
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, body: e.target.value }))}
                placeholder={t('settings.emailTemplatesPage.emailBodyPlaceholder')}
                rows={12}
                required
                readOnly={invitationLocked}
                className="max-[1149px]:text-sm max-[1149px]:rows-8"
              />
            </div>

            <div className="flex items-center max-[1149px]:space-x-1.5">
              <input
                type="checkbox"
                id="is_active"
                checked={templateForm.is_active}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-border text-primary focus:ring-primary max-[1149px]:w-4 max-[1149px]:h-4"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-foreground max-[1149px]:text-xs max-[1149px]:ml-1.5">
                {t('settings.emailTemplatesPage.activeTemplate')}
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border max-[1149px]:pt-3 max-[1149px]:flex-col max-[1149px]:space-y-2 max-[1149px]:space-x-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false)
                  setEditingTemplate(null)
                }}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                className="border-transparent bg-[#19A6A6] text-white hover:bg-[#19A6A6]/90 dark:hover:bg-[#19A6A6]/80 max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {editingTemplate ? t('settings.emailTemplatesPage.updateTemplate') : t('settings.emailTemplatesPage.createTemplate')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && deletingTemplate && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setDeletingTemplate(null)
          }}
          title={t('settings.emailTemplatesPage.deleteEmailTemplate')}
          className="max-[1149px]:max-w-[95vw]"
        >
          <div className="space-y-4 max-[1149px]:space-y-3">
            <p className="text-sm text-muted-foreground max-[1149px]:text-xs">
              {t('settings.emailTemplatesPage.deleteConfirm', { name: deletingTemplate.name })}
            </p>
            <div className="flex justify-end space-x-2 max-[1149px]:flex-col max-[1149px]:space-y-2 max-[1149px]:space-x-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletingTemplate(null)
                }}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('settings.emailTemplatesPage.deleteTemplate')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
